import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable, timer } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import type {
  AuthResult,
  ApiResponse,
  AuthState,
  ForgotPasswordRequest,
  LoginCredentials,
  PasswordResetRequest,
  LoginOtpCredentials,
  LmsLoginRequest,
  LmsLoginResponse,
  SsoOrganization,
} from '../models/auth.models';
import { environment } from '../../../environments/environment.development';
import { ErrorHandlingService } from './error-handling.service';
import * as CryptoJS from 'crypto-js';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../../features/organizations/organization.interface';
import { RolePermissionService } from './role-permission.service';
import { DomSanitizer } from '@angular/platform-browser';
import { AppService } from './app.service';
import { ReturnUrlService } from './return-url.service';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly errorHandlingService = inject(ErrorHandlingService);
  private readonly sanitizer = inject(DomSanitizer);
  public readonly API_URL = environment?.apiUrl;
  private readonly API_URL_LMS = environment?.lmsUrl;
  private readonly USER_KEY = 'user_data';
  private readonly ROLE_KEY = 'user_role';
  private readonly SECRET_KEY = '725a0e9c-1286-411d-b598-5943a6308f0f';
  private readonly ORG_ID_KEY = 'LoggedIn_Org_Id';
  private readonly authStateSubject = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
  });
  readonly orgData = new BehaviorSubject<OrganizationDetailModel | null>(null);
  readonly orgData$ = this.orgData.asObservable();
  readonly userData = new BehaviorSubject<userDetailModel | null>(null);
  readonly userData$ = this.userData.asObservable();
  readonly authState$ = this.authStateSubject.asObservable();
  readonly role = new BehaviorSubject<string[] | []>([]);
  readonly userRoles$ = this.role.asObservable();

  constructor(
    private rbac: RolePermissionService,
    private appService: AppService,
    private returnUrlService: ReturnUrlService,
    private userPreferences: UserPreferencesService
  ) {
    const org = localStorage.getItem('Selected_Org_Data');
    if (org) {
      this.orgData.next(this.decryptData(org));
    }
    const user = localStorage.getItem(this.USER_KEY);
    if (user) {
      this.userData.next(this.decryptData(user));
      this.authStateSubject.next({
        isAuthenticated: true,
        user: this.decryptData(user),
      });
    }
    const role = localStorage.getItem(this.ROLE_KEY);
    if (role) {
      this.role.next(this.decryptData(role));
    }
  }

  sentOtp(credentials: LoginCredentials): Observable<void> {
    return this.http
      .post<
        ApiResponse<AuthResult>
      >(`${this.API_URL}/Auth/sign-in`, credentials, { withCredentials: true })
      .pipe(
        tap(() => {
          const userData = this.encryptData(credentials);
          localStorage.setItem('verify-user', userData);
          localStorage.setItem('isOtpVerified', false.toString());
        }),
        map(() => void 0),
        catchError(error => this.errorHandlingService.handleError(error))
      );
  }

  login(credentials: LoginOtpCredentials): Observable<ApiResponse<AuthResult>> {
    return this.http
      .post<
        ApiResponse<AuthResult>
      >(`${this.API_URL}/Auth/sign-in-validate-otp`, credentials, { withCredentials: true })
      .pipe(
        tap(response => {
          const authResult = response.result;
          const { ...userDataWithoutToken } = authResult;
          localStorage.setItem(
            this.USER_KEY,
            this.encryptData(userDataWithoutToken)
          );
          if (authResult.timeZoneId) {
            this.userPreferences.updateTimezone(authResult.timeZoneId);
          } else {
            const browserTimezone =
              Intl.DateTimeFormat().resolvedOptions().timeZone;
            this.userPreferences.updateTimezone(browserTimezone);
          }
          this.userData.next(userDataWithoutToken);
          this.authStateSubject.next({
            isAuthenticated: true,
            user: {
              id: authResult.id,
              email: authResult.email,
            },
          });
          if (response.result.isSuperAdmin) {
            this.setRole(['System Admin']);
          } else if (authResult.roles && authResult.roles.length) {
            // Set roles returned from API for non-super-admin users
            this.setRole(authResult.roles);
          }
        }),
        map(res => {
          return res;
        }),
        catchError(error => this.errorHandlingService.handleError(error))
      );
  }

  loginWithoutOtp(): Observable<ApiResponse<AuthResult>> {
    return this.http
      .get<
        ApiResponse<AuthResult>
      >(`${this.API_URL}/Auth/get-current-user`, { withCredentials: true })
      .pipe(
        tap(response => {
          const authResult = response.result;
          localStorage.setItem(this.USER_KEY, this.encryptData(authResult));
          if (authResult.timeZoneId) {
            this.userPreferences.updateTimezone(authResult.timeZoneId);
          } else {
            const browserTimezone =
              Intl.DateTimeFormat().resolvedOptions().timeZone;
            this.userPreferences.updateTimezone(browserTimezone);
          }
          this.userData.next(authResult);
          this.authStateSubject.next({
            isAuthenticated: true,
            user: {
              id: authResult.id,
              email: authResult.email,
            },
          });
          if (response.result.isSuperAdmin) {
            this.setRole(['System Admin']);
          } else if (authResult.roles && authResult.roles.length) {
            this.setRole(authResult.roles);
          }
        }),
        map(res => {
          return res;
        }),
        catchError(error => this.errorHandlingService.handleError(error))
      );
  }

  signOut(): void {
    this.appService
      .disconnectSignalR()
      .catch()
      .finally(() => {
        this.http
          .get<void>(`${this.API_URL}/auth/sign-out`, {})
          .pipe(
            catchError(error => this.errorHandlingService.handleError(error))
          )
          .subscribe(() => {
            localStorage.removeItem('idpName');
            localStorage.removeItem('sso-email');
            localStorage.removeItem(this.USER_KEY);
            localStorage.removeItem(this.ROLE_KEY);
            localStorage.removeItem('Selected_Org_Data');
            localStorage.removeItem(this.ORG_ID_KEY);
            localStorage.removeItem('isSupportLogin');
            this.orgData.next(null);
            this.userData.next(null);
            this.role.next([]);
            this.authStateSubject.next({
              isAuthenticated: false,
              user: null,
            });
            this.rbac.setPermissions([]);
            this.returnUrlService.clearReturnUrl();
            this.router.navigate(['/login']);
          });
      });
  }

  forgotPassword(email: string): Observable<void> {
    const request: ForgotPasswordRequest = { email };
    return this.http
      .post<void>(`${this.API_URL}/Auth/forget-password`, request, {
        withCredentials: true,
      })
      .pipe(catchError(error => this.errorHandlingService.handleError(error)));
  }
  getRefreshToken(): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/Auth/validate-refresh-token`, {})
      .pipe(catchError(error => this.errorHandlingService.handleError(error)));
  }

  validateResetToken(token: string): Observable<void> {
    return this.http
      .get<void>(`${this.API_URL}/Auth/validate-reset-token?token=${token}`, {
        withCredentials: true,
      })
      .pipe(catchError(error => this.errorHandlingService.handleError(error)));
  }

  resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Observable<void> {
    const request: PasswordResetRequest = {
      token,
      newPassword,
      confirmPassword,
    };

    return this.http
      .put<void>(`${this.API_URL}/Auth/reset-password`, request, {
        withCredentials: true,
      })
      .pipe(
        tap(() => {
          this.router.navigate(['/login']);
        }),
        catchError(error => this.errorHandlingService.handleError(error))
      );
  }

  updateToken(token: string) {
    if (!token) {
      return;
    }

    timer(500).subscribe(() => {
      this.appService.restartConnection();
    });
  }
  async setRole(role: string[]): Promise<void> {
    this.role.next(role);
    localStorage.setItem(this.ROLE_KEY, this.encryptData(role));
    await lastValueFrom(this.rbac.loadPermissions(role));
  }

  loginLMS(payload: LmsLoginRequest) {
    return this.http
      .post<LmsLoginResponse>(
        `${this.API_URL_LMS}/local/authapi/login.php`,
        payload,
        { withCredentials: true }
      )
      .pipe(catchError(error => this.errorHandlingService.handleError(error)));
  }

  logoutLMS() {
    const url = `https://dev-moodle.insightstobehavior.net/custom_logout.php`;
    this.sanitizer.bypassSecurityTrustResourceUrl(url);
    localStorage.setItem('isLMSLoggedIn', 'false');
  }

  encryptData(
    data: Record<string, unknown>[] | Record<string, unknown> | unknown
  ) {
    return CryptoJS.AES.encrypt(
      JSON.stringify(data),
      this.SECRET_KEY
    ).toString();
  }

  decryptData(encryptedData: string) {
    if (!encryptedData) {
      return null;
    }

    try {
      const decryptedBytes = CryptoJS.AES.decrypt(
        encryptedData,
        this.SECRET_KEY
      );
      const decryptedData = decryptedBytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedData);
    } catch {
      return null;
    }
  }

  getStoredOrgId(): number | null {
    const raw = localStorage.getItem(this.ORG_ID_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  setStoredOrgId(orgId: number | null): void {
    if (orgId) {
      localStorage.setItem(this.ORG_ID_KEY, orgId.toString());
    } else {
      localStorage.removeItem(this.ORG_ID_KEY);
    }
  }

  signInSso(email: string): Observable<ApiResponse<SsoOrganization[]>> {
    const payload = { email };
    return this.http
      .post<
        ApiResponse<SsoOrganization[]>
      >(`${this.API_URL}/auth/sign-in-sso`, payload, { withCredentials: true })
      .pipe(catchError(error => this.errorHandlingService.handleError(error)));
  }

  initiateSamlSso(
    idpName: string,
    returnUrl?: string
  ): Observable<ApiResponse<string>> {
    const params: Record<string, string> = { idpName };
    if (returnUrl) params['returnUrl'] = returnUrl;

    const query = new URLSearchParams(params).toString();
    return this.http
      .get<
        ApiResponse<string>
      >(`${this.API_URL}/api/auth/saml/sso-initiate?${query}`, { withCredentials: true })
      .pipe(catchError(error => this.errorHandlingService.handleError(error)));
  }
}
